// src/api.js
import axios from "axios";

import {
  clearAuthSession,
  getAccessToken,
  getRefreshToken,
  setAuthTokens,
} from "./auth/authStorage";

const RAW =
  (typeof import.meta !== "undefined" ? import.meta.env : {}) || {};

const FALLBACK =
  "https://apisantsystemweb30git-production-318e.up.railway.app";

const USE_PROXY =
  String(RAW.VITE_USE_PROXY || "0").trim() === "1";

const RAW_BASE =
  String(RAW.VITE_API_BASE || FALLBACK).trim();

const BASE =
  USE_PROXY ? undefined : RAW_BASE.replace(/\/+$/, "");

const DEFAULT_PREFIX = "/ssw";
const AUTH_DEBUG_STORAGE_KEY = "santsystem:auth-debug";
const AUTH_DEBUG_MAX_EVENTS = 100;

export const api = axios.create({
  baseURL: BASE,
  timeout: 15000,
});

const refreshHttp = axios.create({
  baseURL: BASE,
  timeout: 15000,
});

export const INTERNAL_TOKEN =
  (RAW.VITE_INTERNAL_TOKEN || "").trim() || null;

let refreshPromise = null;

function saveAuthDebugEvent(event) {
  if (typeof window === "undefined") return;

  try {
    const storage = window.sessionStorage;
    const atual = JSON.parse(
      storage.getItem(AUTH_DEBUG_STORAGE_KEY) || "[]"
    );

    const lista = Array.isArray(atual) ? atual : [];
    lista.push(event);

    storage.setItem(
      AUTH_DEBUG_STORAGE_KEY,
      JSON.stringify(lista.slice(-AUTH_DEBUG_MAX_EVENTS))
    );
  } catch {
    // Diagnóstico nunca deve quebrar a aplicação.
  }
}

function authLog(evento, detalhes = {}, nivel = "info") {
  const registro = {
    timestamp: new Date().toISOString(),
    evento,
    pathname:
      typeof window !== "undefined" ? window.location.pathname : null,
    online:
      typeof navigator !== "undefined" ? navigator.onLine : null,
    ...detalhes,
  };

  saveAuthDebugEvent(registro);

  const prefix = `[AUTH] ${evento}`;

  if (nivel === "error") {
    console.error(prefix, registro);
    return;
  }

  if (nivel === "warn") {
    console.warn(prefix, registro);
    return;
  }

  console.info(prefix, registro);
}

if (typeof window !== "undefined") {
  window.__AUTH_DEBUG__ = {
    get() {
      try {
        return JSON.parse(
          window.sessionStorage.getItem(AUTH_DEBUG_STORAGE_KEY) || "[]"
        );
      } catch {
        return [];
      }
    },

    clear() {
      try {
        window.sessionStorage.removeItem(AUTH_DEBUG_STORAGE_KEY);
      } catch {
        // no-op
      }
    },
  };
}

function joinPath(...segs) {
  return (
    "/" +
    segs
      .filter(Boolean)
      .map((s) => String(s).replace(/^\/+|\/+$/g, ""))
      .filter((s) => s.length)
      .join("/")
  );
}

function isExternalAbsoluteUrl(url) {
  if (!/^https?:\/\//i.test(String(url || ""))) return false;

  try {
    const target = new URL(url);
    const browserOrigin =
      typeof window !== "undefined" ? window.location.origin : undefined;
    const apiOrigin = new URL(BASE || FALLBACK, browserOrigin).origin;

    return target.origin !== apiOrigin;
  } catch {
    return true;
  }
}

function normalizeApiUrl(url) {
  const value = String(url || "");

  if (/^https?:\/\//i.test(value)) return value;

  const normalized = value.replace(/^\/+/, "");

  if (/^(ssw|erp)(\/|$)/i.test(normalized)) {
    return "/" + normalized;
  }

  return joinPath(DEFAULT_PREFIX, normalized);
}

// O token criptográfico da assinatura é independente do JWT de colaboradores.
// Não redirecionar a área pública por uma falha de autenticação de sessão interna.
function isPublicContractPage() {
  return typeof window !== "undefined" &&
    /^\/assinar\/[^/]+\/?$/i.test(window.location.pathname);
}

function redirectToLogin() {
  if (typeof window === "undefined") return;
  if (window.location.pathname === "/" || isPublicContractPage()) return;

  authLog(
    "REDIRECT_LOGIN",
    { destino: "/" },
    "warn"
  );

  window.location.replace("/");
}

function getErrorDetails(error) {
  return {
    status: error?.response?.status ?? null,
    codigo:
      error?.response?.data?.codigo ??
      error?.response?.data?.code ??
      null,
    mensagem:
      error?.response?.data?.message ??
      error?.message ??
      null,
    axiosCode: error?.code ?? null,
    url: error?.config?.url ?? null,
    method:
      error?.config?.method?.toUpperCase?.() ?? null,
  };
}

async function refreshSession() {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    const error = new Error("Refresh token indisponível.");
    error.code = "REFRESH_TOKEN_UNAVAILABLE";

    authLog(
      "REFRESH_ABORTADO_SEM_TOKEN",
      { possuiAccessToken: Boolean(getAccessToken()) },
      "warn"
    );

    throw error;
  }

  if (refreshPromise) {
    authLog("REFRESH_AGUARDANDO_PROMISE_EXISTENTE");
    return refreshPromise;
  }

  authLog("REFRESH_INICIADO", {
    possuiAccessToken: Boolean(getAccessToken()),
    possuiRefreshToken: true,
  });

  refreshPromise = refreshHttp
    .post("/ssw/token/refresh", { refreshToken })
    .then(({ data }) => {
      if (!data?.token || !data?.refreshToken) {
        const error = new Error(
          "Resposta inválida ao renovar sessão."
        );

        error.code = "INVALID_REFRESH_RESPONSE";

        authLog(
          "REFRESH_RESPOSTA_INVALIDA",
          {
            possuiTokenResposta: Boolean(data?.token),
            possuiRefreshResposta: Boolean(data?.refreshToken),
          },
          "error"
        );

        throw error;
      }

      setAuthTokens({
        token: data.token,
        refreshToken: data.refreshToken,
      });

      authLog("REFRESH_SUCESSO", {
        possuiNovoAccessToken: true,
        possuiNovoRefreshToken: true,
        refreshTokenExpira: data?.refreshTokenExpira ?? null,
      });

      return data.token;
    })
    .catch((error) => {
      authLog(
        "REFRESH_FALHOU",
        getErrorDetails(error),
        "error"
      );

      throw error;
    })
    .finally(() => {
      refreshPromise = null;
      authLog("REFRESH_FINALIZADO");
    });

  return refreshPromise;
}

api.interceptors.request.use(
  (cfg) => {
    const originalUrl = String(cfg.url || "");
    const externalAbsoluteUrl = isExternalAbsoluteUrl(originalUrl);

    cfg.url = normalizeApiUrl(originalUrl);

    const token = getAccessToken();

    if (token && !externalAbsoluteUrl && !cfg.headers?.Authorization) {
      cfg.headers = cfg.headers || {};
      cfg.headers.Authorization = `Bearer ${token}`;
    }

    const full = /^https?:\/\//i.test(cfg.url)
      ? cfg.url
      : `${cfg.baseURL || ""}${cfg.url}`;

    // Não logar body/payload para evitar exposição de dados sensíveis.
    console.debug(
      `[API →] ${cfg.method?.toUpperCase()} ${full}`
    );

    return cfg;
  },
  (error) => {
    authLog(
      "REQUEST_INTERCEPTOR_ERRO",
      getErrorDetails(error),
      "error"
    );

    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (res) => res,

  async (err) => {
    const original = err.config || {};
    const status = err.response?.status;
    const url = String(original.url || "");
    const full = `${original.baseURL || ""}${url}`;

    if (status === 404) {
      console.error(`[API 404] ${full}`, err.response?.data);
    }

    if (
      err.code === "ERR_NETWORK" ||
      err.message === "Network Error" ||
      !err.response
    ) {
      err.isNetwork = true;

      authLog(
        "ERRO_REDE_SESSAO_PRESERVADA",
        {
          ...getErrorDetails(err),
          url: full,
        },
        "error"
      );

      return Promise.reject(err);
    }

    const isRefreshRequest =
      /\/token\/refresh(?:\?|$)/i.test(url);

    const externalAbsoluteUrl =
      isExternalAbsoluteUrl(url);

    const canRefresh =
      Boolean(getRefreshToken());

    if (status === 401) {
      authLog(
        "RESPOSTA_401",
        {
          url: full,
          method: original.method?.toUpperCase?.() ?? null,
          isRefreshRequest,
          externalAbsoluteUrl,
          canRefresh,
          retryJaExecutado: Boolean(original._retryAuth),
          skipAuthRefresh: Boolean(original._skipAuthRefresh),
          resposta: err.response?.data ?? null,
        },
        "warn"
      );
    }

    if (
      status === 401 &&
      !isPublicContractPage() &&
      !externalAbsoluteUrl &&
      !original._retryAuth &&
      !original._skipAuthRefresh &&
      !isRefreshRequest &&
      canRefresh
    ) {
      original._retryAuth = true;

      authLog(
        "401_INICIANDO_REFRESH",
        {
          url: full,
          method: original.method?.toUpperCase?.() ?? null,
        },
        "warn"
      );

      try {
        const newAccessToken =
          await refreshSession();

        original.headers =
          original.headers || {};

        original.headers.Authorization =
          `Bearer ${newAccessToken}`;

        authLog(
          "REQUEST_REPETIDA_APOS_REFRESH",
          {
            url: full,
            method: original.method?.toUpperCase?.() ?? null,
          }
        );

        return api.request(original);
      } catch (refreshError) {
        const refreshStatus =
          refreshError?.response?.status;

        const detalhes =
          getErrorDetails(refreshError);

        if (
          refreshStatus === 401 ||
          refreshStatus === 403
        ) {
          authLog(
            "LOGOUT_REFRESH_REJEITADO",
            {
              ...detalhes,
              requisicaoOriginal: full,
            },
            "error"
          );

          clearAuthSession();
          redirectToLogin();
        } else {
          authLog(
            "REFRESH_FALHA_TEMPORARIA_SESSAO_PRESERVADA",
            {
              ...detalhes,
              requisicaoOriginal: full,
            },
            "error"
          );
        }

        return Promise.reject(refreshError);
      }
    }

    if (
      status === 401 &&
      !isPublicContractPage() &&
      !canRefresh &&
      !original._skipAuthRefresh
    ) {
      authLog(
        "LOGOUT_401_SEM_REFRESH_TOKEN",
        {
          url: full,
          method: original.method?.toUpperCase?.() ?? null,
          resposta: err.response?.data ?? null,
        },
        "error"
      );

      clearAuthSession();
      redirectToLogin();
    }

    return Promise.reject(err);
  }
);

export async function logoutSession() {
  const refreshToken =
    getRefreshToken();

  authLog(
    "LOGOUT_SOLICITADO_USUARIO",
    {
      possuiRefreshToken: Boolean(refreshToken),
    }
  );

  try {
    if (refreshToken) {
      await refreshHttp.post(
        "/ssw/token/logout",
        { refreshToken }
      );

      authLog("LOGOUT_SERVIDOR_SUCESSO");
    }
  } catch (error) {
    authLog(
      "LOGOUT_SERVIDOR_FALHOU",
      getErrorDetails(error),
      "warn"
    );
  } finally {
    clearAuthSession();
    authLog("LOGOUT_LOCAL_CONCLUIDO");
  }
}

try {
  if (typeof window !== "undefined") {
    const wsOrigin = USE_PROXY
      ? window.location.origin
      : new URL(BASE || FALLBACK).origin;

    window.__AXIOS__ = {
      baseURL: wsOrigin,
    };
  }
} catch {
  // no-op
}
